import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Backdrop,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CustomerManagementList from "../../../Screens/Customers/CustomerManagementList";
import { Doughnut, Line } from "react-chartjs-2";
import "../../../styles/admin-panel.scss";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboardDataRequest } from "../../../redux/Dashboard/DashboardAction";
import {
  fetchAirportsRequest,
  fetchAllAirportRequest,
} from "../../../redux/Airports/actions/airportActions";
import {
  fetchAllServiceRequest,
  serviceViewAllRequest,
} from "../../../redux/Service/ServiceAction";
import { useNavigate } from "react-router-dom";
import { BOOKINGS } from "../Routes/AppRoutes";
import { enGB } from "date-fns/locale";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const DashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role;
  console.log("user", userRole);
  const { data, loading, error } = useSelector((state) => state.dashboard);
  console.log("data", data);
  const { airportsList } = useSelector((state) => state.airport || {});
  const serviceDetail = useSelector(
    (state) => state.services?.ServiceDetails || {}
  );

  // Add initial loading state to track first load
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false); // New state for filter loading
  const [dataFetched, setDataFetched] = useState({
    dashboard: false,
    airports: false,
    services: false,
  });

  const today = new Date();
  const currentYear = today.getFullYear();

  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [fromYear, setFromYear] = useState(currentYear - 1);
  const [toYear, setToYear] = useState(currentYear);
  const [selectedAirport, setSelectedAirport] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    fromYear: null,
    toYear: null,
    airport: null,
    service: null,
  });

  // Services filtered by selected airport
  const [filteredServices, setFilteredServices] = useState([]);

  const airportsData = airportsList?.data || [];
  const servicesData = serviceDetail?.data || [];

  // Helper function to generate year options
  const generateYearOptions = () => {
    const years = [];
    const maxYear = currentYear + 1; // Allow next year
    const minYear = 2020; // Adjust as needed

    for (let year = maxYear; year >= minYear; year--) {
      years.push(year);
    }
    return years;
  };

  // Initial data fetching with proper sequencing
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch all required data
        await Promise.all([
          dispatch(fetchDashboardDataRequest()),
          dispatch(fetchAllAirportRequest()),
          dispatch(fetchAllServiceRequest()),
        ]);

        // Mark data as fetched
        setDataFetched({
          dashboard: true,
          airports: true,
          services: true,
        });
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        // Set initial loading to false after a small delay to ensure smooth transition
        setTimeout(() => {
          setInitialLoading(false);
        }, 500);
      }
    };

    fetchInitialData();
  }, [dispatch]);

  // Track when individual data pieces are loaded
  useEffect(() => {
    if (data && !loading) {
      setDataFetched((prev) => ({ ...prev, dashboard: true }));
      setFilterLoading(false); // Stop filter loading when data is received
    }
  }, [data, loading]);

  useEffect(() => {
    if (airportsList?.data) {
      setDataFetched((prev) => ({ ...prev, airports: true }));
    }
  }, [airportsList]);

  useEffect(() => {
    if (serviceDetail?.data) {
      setDataFetched((prev) => ({ ...prev, services: true }));
    }
  }, [serviceDetail]);

  // Filter services based on selected airport
  useEffect(() => {
    if (selectedAirport) {
      const selectedAirportObj = airportsData.find(
        (airport) => airport.id === selectedAirport
      );
      if (selectedAirportObj && selectedAirportObj.AiportServices) {
        // Extract services from the selected airport's AiportServices
        const airportServices = selectedAirportObj.AiportServices.map(
          (airportService) => ({
            id: airportService.Service.id,
            services: airportService.Service.services,
            type_of_name: airportService.Service.type_of_name,
            type_of_service: airportService.Service.type_of_service,
            arrival_departure_type:
              airportService.Service.arrival_departure_type,
            transit_type_of_service:
              airportService.Service.transit_type_of_service,
            price: airportService.price,
          })
        );
        setFilteredServices(airportServices);
      } else {
        // If airport doesn't have AiportServices, fallback to all services
        setFilteredServices(servicesData);
      }
      // Reset selected service when airport changes
      setSelectedService("");
    } else {
      // Show all services when no airport is selected
      setFilteredServices(servicesData);
    }
  }, [selectedAirport, airportsData, servicesData]);

  const handleCardClick = () => {
    navigate(BOOKINGS);
  };

  // Check if any filters are selected
  const hasSelectedFilters = () => {
    return (
      (startDate && endDate) ||
      (fromYear && toYear && !startDate && !endDate) ||
      selectedAirport !== "" ||
      selectedService !== ""
    );
  };

  // Check if any filters are applied
  const hasAppliedFilters = () => {
    return (
      appliedFilters.startDate ||
      appliedFilters.endDate ||
      appliedFilters.fromYear ||
      appliedFilters.toYear ||
      appliedFilters.airport ||
      appliedFilters.service
    );
  };

  const formatDateForAPI = (date) => {
    if (!date) return null;

    // Get the local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Updated handleApplyFilters function with year logic
  const handleApplyFilters = () => {
    setFilterLoading(true); // Start filter loading

    const selectedAirportObj = airportsData.find(
      (airport) => airport.id === selectedAirport
    );
    const selectedServiceObj = filteredServices.find(
      (service) => service.id === selectedService
    );

    const filters = {
      startDate,
      endDate,
      fromYear,
      toYear,
      airport: selectedAirportObj || null,
      service: selectedServiceObj || null,
    };

    setAppliedFilters(filters);

    // Prepare filter parameters for API
    const filterParams = {};

    // Handle date filtering with proper timezone handling
    // If dates are selected, use dates and ignore years
    if (startDate && endDate) {
      filterParams.fromDate = formatDateForAPI(startDate);
      filterParams.toDate = formatDateForAPI(endDate);
      // Don't include year parameters when dates are selected
    } else if (fromYear && toYear) {
      // Only use year parameters if dates are not selected
      filterParams.fromYear = fromYear;
      filterParams.toYear = toYear;
    }

    // Handle airport filtering
    if (selectedAirport) {
      filterParams.airport_ids = [selectedAirport];
    }

    // Handle service filtering
    if (selectedService) {
      filterParams.service_ids = [selectedService];
    }

    console.log("Filter parameters being sent:", filterParams); // For debugging

    // Dispatch action with filter parameters
    dispatch(fetchDashboardDataRequest(filterParams));
  };

  const handleClearFilters = () => {
    setFilterLoading(true); // Start filter loading

    setStartDate(null);
    setEndDate(null);
    setFromYear(currentYear - 1);
    setToYear(currentYear);
    setSelectedAirport("");
    setSelectedService("");
    setAppliedFilters({
      startDate: null,
      endDate: null,
      fromYear: null,
      toYear: null,
      airport: null,
      service: null,
    });

    // Reset filtered services to all services
    setFilteredServices(servicesData);

    // Dispatch without any filters to get all data
    dispatch(fetchDashboardDataRequest({}));
  };

  const handleAirportChange = (event) => {
    setSelectedAirport(event.target.value);
  };

  const handleServiceChange = (event) => {
    setSelectedService(event.target.value);
  };

  const handleFromYearChange = (event) => {
    const selectedFromYear = event.target.value;
    setFromYear(selectedFromYear);
    // Ensure toYear is not less than fromYear
    if (toYear < selectedFromYear) {
      setToYear(selectedFromYear);
    }
  };

  const handleToYearChange = (event) => {
    setToYear(event.target.value);
  };

  const getServiceDisplayName = (service) => {
    if (service.arrival_departure_type !== 3) {
      return `${service.services} - ${service.type_of_name} - ${service.type_of_service}`;
    } else {
      return `${service.services} - ${service.type_of_name} - ${
        service.type_of_service
      } - ${service.transit_type_of_service || "Unknown"}`;
    }
  };

  const getFilterChips = () => {
    const chips = [];

    if (appliedFilters.startDate && appliedFilters.endDate) {
      const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const dateLabel = `${formatDate(appliedFilters.startDate)} - ${formatDate(
        appliedFilters.endDate
      )}`;
      chips.push({
        key: "date",
        label: `Date: ${dateLabel}`,
        onDelete: () => {
          setStartDate(null);
          setEndDate(null);
          setAppliedFilters((prev) => ({
            ...prev,
            startDate: null,
            endDate: null,
          }));
        },
      });
    } else if (appliedFilters.fromYear && appliedFilters.toYear) {
      chips.push({
        key: "year",
        label: `Year: ${appliedFilters.fromYear} - ${appliedFilters.toYear}`,
        onDelete: () => {
          setFromYear(currentYear - 1);
          setToYear(currentYear);
          setAppliedFilters((prev) => ({
            ...prev,
            fromYear: null,
            toYear: null,
          }));
        },
      });
    }

    if (appliedFilters.airport) {
      chips.push({
        key: `airport-${appliedFilters.airport.id}`,
        label: `Airport: ${appliedFilters.airport.name}`,
        onDelete: () => {
          setSelectedAirport("");
          setAppliedFilters((prev) => ({ ...prev, airport: null }));
        },
      });
    }

    if (appliedFilters.service) {
      chips.push({
        key: `service-${appliedFilters.service.id}`,
        label: `Service: ${getServiceDisplayName(appliedFilters.service)}`,
        onDelete: () => {
          setSelectedService("");
          setAppliedFilters((prev) => ({ ...prev, service: null }));
        },
      });
    }

    return chips;
  };

  // Show initial loading backdrop only on first load
  if (initialLoading) {
    return (
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
        }}
        open={true}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#4CAF50" }} />
        </Box>
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Error loading dashboard data: {error}</Alert>
    );
  }

  const userRolesData = {
    labels: ["Arrival", "Departure", "Transit"],
    datasets: [
      {
        data: [
          data?.count?.arrivalServicesCount || 0,
          data?.count?.departureServicesCount || 0,
          data?.count?.transitServicesCount || 0,
        ],
        backgroundColor: ["#4CAF50", "#FF9800", "#FF6384"],
        hoverBackgroundColor: ["#45D659", "#FFC947", "#FF7397"],
      },
    ],
  };

  const bookingStatusData = {
    labels: ["Total", "Completed", "Cancelled"],
    datasets: [
      {
        data: [
          data?.count?.totalBookingCount || 0,
          data?.count?.totalCompletedBookingCount || 0,
          data?.count?.totalCancelBookingCount || 0,
        ],
        backgroundColor: ["#FF6384", "#4CAF50", "#FFCE56"],
        hoverBackgroundColor: ["#FF7397", "#45D659", "#FFD970"],
      },
    ],
  };

  const statisticsData = [
    { metric: "Total Users", value: data?.count?.usersCount || 0 },
    { metric: "Total Bookings", value: data?.count?.totalBookingCount || 0 },
    {
      metric: "Completed Bookings",
      value: data?.count?.totalCompletedBookingCount || 0,
    },
    {
      metric: "Total Revenue",
      value: `₹ ${Number(data?.count?.totalRevenue || 0).toFixed(2)}`,
    },
  ];

  console.log("data?.count", data?.count?.usersCount);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box
        className="admin-panel-box"
        sx={{ padding: 2, position: "relative" }}
      >
        {/* Filter Loading Backdrop */}
        {filterLoading && (
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            }}
            open={true}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={50} sx={{ color: "#4CAF50" }} />
            </Box>
          </Backdrop>
        )}

        {/* Inline loading indicator for subsequent loads (keeping the original) */}
        {loading && !initialLoading && !filterLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 1000,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: 2,
              p: 2,
              boxShadow: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} sx={{ color: "#4CAF50" }} />
            </Box>
          </Box>
        )}

        {/* Filter Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Dashboard Filters
            </Typography>

            <Grid container spacing={3} alignItems="center">
              {/* Date Range Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  maxDate={today}
                  inputFormat="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate}
                  maxDate={today}
                  disabled={!startDate}
                  inputFormat="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </Grid>

              {/* OR Divider */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", my: 1 }}>
                  <Divider sx={{ flexGrow: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{ mx: 2, color: "text.secondary" }}
                  >
                    OR
                  </Typography>
                  <Divider sx={{ flexGrow: 1 }} />
                </Box>
              </Grid>

              {/* Year Range Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>From Year</InputLabel>
                  <Select
                    value={fromYear}
                    onChange={handleFromYearChange}
                    label="From Year"
                    disabled={!!(startDate && endDate)}
                  >
                    {generateYearOptions().map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>To Year</InputLabel>
                  <Select
                    value={toYear}
                    onChange={handleToYearChange}
                    label="To Year"
                    disabled={!!(startDate && endDate)}
                  >
                    {generateYearOptions()
                      .filter((year) => year >= fromYear)
                      .map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Airport Single Select */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Airport</InputLabel>
                  <Select
                    value={selectedAirport}
                    onChange={handleAirportChange}
                    label="Airport"
                  >
                    <MenuItem value="">
                      <em>All Airports</em>
                    </MenuItem>
                    {airportsData.map((airport) => (
                      <MenuItem key={airport.id} value={airport.id}>
                        {airport.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Service Single Select - Filtered by Airport */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Service</InputLabel>
                  <Select
                    value={selectedService}
                    onChange={handleServiceChange}
                    label="Service"
                    disabled={selectedAirport && filteredServices.length === 0}
                  >
                    <MenuItem value="">
                      <em>All Services</em>
                    </MenuItem>
                    {filteredServices.map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        {getServiceDisplayName(service)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filter Buttons */}
              <Grid item xs={12} md={12}>
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    size="small"
                    disabled={!hasSelectedFilters() || filterLoading}
                    sx={{
                      bgcolor: "#4CAF50",
                      "&:hover": { bgcolor: "#45D659" },
                      "&:disabled": { bgcolor: "#E0E0E0", color: "#9E9E9E" },
                    }}
                  >
                    {filterLoading ? (
                      <>
                        <CircularProgress
                          size={16}
                          sx={{ color: "#9E9E9E", mr: 1 }}
                        />
                        Applying...
                      </>
                    ) : (
                      "Apply Filters"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    size="small"
                    disabled={!hasAppliedFilters() || filterLoading}
                    sx={{
                      borderColor: "#FF9800",
                      color: "#FF9800",
                      "&:hover": {
                        borderColor: "#FFC947",
                        color: "#FFC947",
                        bgcolor: "rgba(255, 152, 0, 0.04)",
                      },
                      "&:disabled": {
                        borderColor: "#E0E0E0",
                        color: "#9E9E9E",
                      },
                    }}
                  >
                    {filterLoading ? (
                      <>
                        <CircularProgress
                          size={16}
                          sx={{ color: "#9E9E9E", mr: 1 }}
                        />
                        Clearing...
                      </>
                    ) : (
                      "Clear All"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {getFilterChips().length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  Active Filters:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {getFilterChips().map((chip) => (
                    <Chip
                      key={chip.key}
                      label={chip.label}
                      onDelete={chip.onDelete}
                      sx={{
                        bgcolor: "#4CAF50",
                        color: "white",
                        "&:hover": { bgcolor: "#45D659" },
                        "& .MuiChip-deleteIcon": { color: "white" },
                      }}
                      variant="filled"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Dashboard Cards */}
        <Grid
          container
          spacing={3}
          sx={{ display: "flex", justifyContent: "center", mb: 4 }}
        >
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                height: "100%",
                borderLeft: "4px solid #4CAF50",
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  sx={{ fontWeight: 500 }}
                >
                  Total Users
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "#4CAF50", my: 1 }}
                >
                  {data?.count?.usersCount || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Admins: {data?.count?.adminCount || 0} • Staff:{" "}
                  {data?.count?.staffsCount || 0} • Vendors:{" "}
                  {data?.count?.vendorsCount || 0} • Customers:{" "}
                  {data?.count?.customersCount || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                height: "100%",
                borderLeft: "4px solid #FF9800",
                cursor: "pointer",
              }}
              onClick={handleCardClick}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  sx={{ fontWeight: 500 }}
                >
                  Total Bookings
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "#FF9800", my: 1 }}
                >
                  {data?.count?.fullTotalBooking || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Today's Bookings: {data?.count?.todayBookingCount || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                height: "100%",
                borderLeft: "4px solid #4b77f0ff",
                cursor: "pointer",
              }}
              onClick={handleCardClick}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  sx={{ fontWeight: 500 }}
                >
                  Total Service
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "#4b77f0ff", my: 1 }}
                >
                  {data?.count?.fullTotalServiceCount || 0}
                </Typography>
             
              </CardContent>
            </Card>
          </Grid>
          {userRole === "Admin" && (
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  height: "100%",
                  borderLeft: "4px solid #FF6384",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "#FF6384", my: 1 }}
                  >
                    ₹ {Number(data?.count?.fullTotalRevenue || 0).toFixed(2)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    Today's Revenue: {data?.count?.todayRevenue || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                height: "100%",
                borderLeft: "4px solid #9C27B0",
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  sx={{ fontWeight: 500 }}
                >
                  Completed Booking
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "#9C27B0", my: 1 }}
                >
                  {data?.count?.totalCompletedBookingCount || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Cancellations: {data?.count?.totalCancelBookingCount || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Charts */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Service Distribution
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  height: 300,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Doughnut
                  data={userRolesData}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                      legend: {
                        position: "bottom",
                      },
                      title: {
                        display: false,
                      },
                    },
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Booking Status
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  height: 300,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Doughnut
                  data={bookingStatusData}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                      legend: {
                        position: "bottom",
                      },
                      title: {
                        display: false,
                      },
                    },
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Customer Management List */}
        <CustomerManagementList />

        <Divider sx={{ my: 4 }} />

        {/* Additional Dashboard Sections */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Booking Management
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#FF6384" }}
                      >
                        {data?.count?.totalBookingCount || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#4CAF50" }}
                      >
                        {data?.count?.totalCompletedBookingCount || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Completed
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#FFCE56" }}
                      >
                        {data?.count?.totalCancelBookingCount || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Cancelled
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Vendor Management
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#9C27B0" }}
                      >
                        {data?.count?.vendorsCount || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Vendors
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        borderRadius: 1,
                        bgcolor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#FF9800" }}
                      >
                        10%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Avg. Discount
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Statistics Table */}
        <Paper
          elevation={3}
          sx={{ borderRadius: 2, overflow: "hidden", mb: 4 }}
        >
          <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Statistics Reports
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Metric</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statisticsData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>{row.metric}</TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: index === 3 ? "bold" : "normal",
                      color: index === 3 ? "#4CAF50" : "inherit",
                    }}
                  >
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
